// Persistent bottom tab bar — primary navigation for mobile. Lives in
// the *easy zone* (bottom 1/3, ~95% one-handed accuracy per Hoober).
// Frequently-used switches between major sections of the app:
//
// Player tabSet (5 tabs):
//   🛡️ Stats      — portrait, HP/mana/XP, status effects
//   📜 Skills     — skills + saving throws
//   ✨ Spells     — spell list + techniques
//   🎒 Inventory  — equipment + inventory
//   ⚔️ Adventure  — Travel / Journal / Combat / Shop / Crafting / Loot
//
// DM tabSet (4 tabs):
//   👥 Party      — character cards + combat
//   🌍 World      — food / travel log / systems overview
//   🛠️ Tools     — messages / shop / crafting / loot
//   🎯 Session    — session mode + timeline
//
// Each tap-target is ≥56px wide (Spotify case study found icon-only
// loses ~30% feature discoverability — we always pair icon + label).
// Active tab gets the accent color + a 2px top border.
//
// Body content above the nav must include `paddingBottom: 70px` to
// clear the bar (60px nav + ~10px breathing). The nav itself respects
// `env(safe-area-inset-bottom)` for iOS home-indicator avoidance.

import React from 'react';
import useChromeOffset from './useChromeOffset';
import useReducedMotion from './useReducedMotion';
import { t, DUR_BASE, DUR_SLOW, EASE_STANDARD, SPRING_OVERSHOOT } from './transitions';

// 2026-05-11 F1 fix: tab icons use plain Unicode codepoints (no FE0F
// variation selector) so font fallback on Android / older browsers
// renders the intended glyph rather than a literal arrow or box.
// `⚔` (U+2694) without VS is the crossed-swords codepoint that
// previously rendered as `→` on the user's device.
const PLAYER_TABS = [
  { id: 'stats',     icon: '🛡', label: 'Stats' },
  { id: 'skills',    icon: '📜', label: 'Skills' },
  { id: 'spells',    icon: '✨', label: 'Spells' },
  { id: 'inventory', icon: '🎒', label: 'Inventory' },
  { id: 'adventure', icon: '⚔', label: 'Adventure' },
];

const DM_TABS = [
  { id: 'party',   icon: '👥', label: 'Party' },
  { id: 'world',   icon: '🌍', label: 'World' },
  { id: 'tools',   icon: '🛠', label: 'Tools' },
  { id: 'session', icon: '🎯', label: 'Session' },
];

export default function MobileBottomNav({ tabSet = 'player', activeTab, onTabChange, badges = {} }) {
  const tabs = tabSet === 'dm' ? DM_TABS : PLAYER_TABS;
  // 2026-04-29 — A.4 polish: indicator slides between tabs instead of
  // border-top jumping per-tab. Single absolute-positioned bar that
  // translates left to the active tab's slot. Equal-width tabs make
  // the math trivial — no measurement needed.
  const activeIdx = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));
  const indicatorPct = 100 / tabs.length;
  const chromeOffset = useChromeOffset();
  const reduceMotion = useReducedMotion();

  return (
    <nav
      aria-label={tabSet === 'dm' ? 'DM navigation' : 'Player navigation'}
      style={{
        position: 'fixed',
        bottom: chromeOffset,
        left: 0,
        right: 0,
        background: 'var(--bg2, #1e1624)',
        borderTop: '1px solid var(--bs, #2a2033)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-around',
        zIndex: 100,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.25)',
        // No transition on bottom — visualViewport changes are themselves
        // animated by the browser; adding our own transition makes the
        // nav lag visibly behind the chrome.
      }}
    >
      {/* Sliding indicator — single 2px bar at the top of the nav that
          translates to the active tab. Spring physics for the slide. */}
      {activeTab && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: `${activeIdx * indicatorPct}%`,
            width: `${indicatorPct}%`,
            height: '2px',
            background: 'var(--ap, #6b3fa0)',
            transition: reduceMotion ? 'none' : t(['left'], DUR_SLOW, SPRING_OVERSHOOT),
            pointerEvents: 'none',
          }}
        />
      )}
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const badge = badges[tab.id];
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange?.(tab.id)}
            aria-label={tab.label}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: 1,
              minHeight: '56px',
              minWidth: '56px',
              background: 'transparent',
              border: 'none',
              padding: '4px 2px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              cursor: 'pointer',
              color: isActive ? 'var(--tp, #d4baff)' : 'var(--tm, #888)',
              fontSize: '0.65rem',
              fontWeight: isActive ? 700 : 500,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              transition: reduceMotion ? 'none' : t(['color'], DUR_BASE, EASE_STANDARD),
              position: 'relative',
            }}
          >
            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>{tab.icon}</span>
            <span>{tab.label}</span>
            {badge > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '20%',
                  minWidth: '16px',
                  height: '16px',
                  padding: '0 4px',
                  background: '#c92727',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
